import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { Button } from '../components/ui/button';
import { ArrowRight, Check, Euro, CreditCard, Users, HelpCircle, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';

export default function TuitionPage() {
  const { t, language } = useLanguage();

  const pricingPlans = language === 'fr' ? [
    {
      title: '1 Cours',
      grades: 'Grande Section à Terminale',
      price: '1 995',
      color: '#2D78F6',
      details: [
        { label: 'Frais de dossier et inscription', value: '75 €' },
        { label: 'Acompte pour valider l\'inscription', value: '295 €' },
        { label: 'Option paiement échelonné', value: '1, 2 ou 10 mensualités', note: 'après versement de l\'acompte' },
      ],
    },
    {
      title: '2 Cours',
      grades: 'Lycée uniquement (3ème à Terminale)',
      price: '3 990',
      color: '#14274B',
      details: [
        { label: 'Frais de dossier et inscription', value: '75 €' },
        { label: 'Acompte pour valider l\'inscription', value: '390 €' },
        { label: 'Option paiement échelonné', value: '1, 2 ou 10 mensualités', note: 'après versement de l\'acompte' },
      ],
    },
  ] : [
    {
      title: '1 Course',
      grades: 'Kindergarten to 12th grade',
      price: '1,995',
      color: '#2D78F6',
      details: [
        { label: 'Registration & application fee', value: '€75' },
        { label: 'Deposit to confirm enrollment', value: '€295' },
        { label: 'Installment plan option', value: '1, 2 or 10 installments', note: 'after deposit payment' },
      ],
    },
    {
      title: '2 Courses',
      grades: 'High School only (3ème to Terminale)',
      price: '3,990',
      color: '#14274B',
      details: [
        { label: 'Registration & application fee', value: '€75' },
        { label: 'Deposit to confirm enrollment', value: '€390' },
        { label: 'Installment plan option', value: '1, 2 or 10 installments', note: 'after deposit payment' },
      ],
    },
  ];

  const paymentTerms = language === 'fr' ? [
    { icon: CreditCard, text: 'Paiements par carte bancaire uniquement' },
    { icon: Euro, text: '3% de frais de transaction sont appliqués' },
    { icon: Calendar, text: 'Le paiement est dû le 15 de chaque mois' },
  ] : [
    { icon: CreditCard, text: 'Credit card payments only' },
    { icon: Euro, text: '3% transaction fee applied' },
    { icon: Calendar, text: 'Payment due on the 15th of each month' },
  ];

  const siblingDiscounts = language === 'fr' ? [
    {
      discount: '10%',
      child: '2ème enfant',
      desc: 'Réduction sur les frais les plus bas des deux. Si les frais sont identiques, les 10% seront appliqués à un des deux enfants.',
    },
    {
      discount: '20%',
      child: '3ème enfant',
      desc: 'Réduction sur les frais les plus bas des trois. Si les frais sont identiques, les 20% seront appliqués à un des trois enfants.',
    },
  ] : [
    {
      discount: '10%',
      child: '2nd child',
      desc: 'Discount applied to the lower tuition of the two. If tuition is the same, 10% applies to one child.',
    },
    {
      discount: '20%',
      child: '3rd child',
      desc: 'Discount applied to the lowest tuition of the three. If tuition is the same, 20% applies to one child.',
    },
  ];

  const includes = language === 'fr' ? [
    'Enseignement en direct avec des enseignants qualifiés',
    'Curriculum et matériel complets',
    'Évaluations régulières et bulletins de progression',
    'Communication et soutien aux parents',
    'Accès à la communauté OFA France',
    'Orientation vers le supérieur (France et international)',
  ] : [
    'Live instruction with qualified teachers',
    'Full curriculum and materials',
    'Regular assessments and progress reports',
    'Parent communication and support',
    'Access to OFA France community',
    'Higher education guidance (France and international)',
  ];

  return (
    <div data-testid="tuition-page" className="min-h-screen bg-white">
      <SEO path="/tuition" />

      {/* Schema Offer */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "Offer",
          "name": "OFA France — 1 cours par semaine (1h30)",
          "description": "Programme américain en ligne accrédité COGNIA — 1 cours de 1h30 par semaine sur 32 semaines.",
          "price": "1995",
          "priceCurrency": "EUR",
          "priceValidUntil": "2027-08-31",
          "availability": "https://schema.org/InStock",
          "url": "https://ofalycee.fr/tuition",
          "offeredBy": { "@type": "Organization", "name": "OFA France", "url": "https://ofalycee.fr" }
        },
        {
          "@context": "https://schema.org",
          "@type": "Offer",
          "name": "OFA France — 2 cours par semaine (3h)",
          "description": "Programme américain en ligne accrédité COGNIA — 2 cours de 1h30 par semaine (3h) sur 32 semaines.",
          "price": "3990",
          "priceCurrency": "EUR",
          "priceValidUntil": "2027-08-31",
          "availability": "https://schema.org/InStock",
          "url": "https://ofalycee.fr/tuition",
          "offeredBy": { "@type": "Organization", "name": "OFA France", "url": "https://ofalycee.fr" }
        }
      ])}} />

      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#2D78F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.tuition.heroTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-white/80 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.tuition.heroSubtitle}
          </motion.p>
        </div>
      </section>

      <QuickLinksBar />

      {/* Intro SEO */}
      <section className="py-12 bg-[#E7EAEF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#14274B] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'OFA France propose une tarification transparente et accessible pour une scolarité américaine de qualité. Nos frais couvrent l\'ensemble du programme académique : cours en direct avec des enseignants américains certifiés, accès à notre plateforme Canvas, bulletins semestriels, et le suivi individualisé de chaque élève. 1 995 €/an pour 1 cours (1h30/semaine) ou 3 990 €/an pour 2 cours (3h/semaine), sur 32 semaines. Des facilités de paiement sont disponibles pour toutes les familles.'
              : 'OFA France offers transparent and accessible pricing for quality American education. Our fees cover the entire academic program: live classes with certified American teachers, access to our Canvas platform, semester reports, and individualized student monitoring. €1,995/year for 1 course (1.5h/week) or €3,990/year for 2 courses (3h/week), over 32 weeks. Payment plans are available for all families.'}
          </p>
          <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Des réductions sont proposées pour les familles inscrivant plusieurs enfants. Un acompte confirme la place de votre enfant pour l\'année scolaire, le solde pouvant être réglé en plusieurs versements. Contactez-nous pour toute question sur les modalités de paiement ou les aides disponibles.'
              : 'Discounts are offered for families enrolling multiple children. A deposit confirms your child\'s place for the school year, with the balance payable in several installments. Contact us for any questions about payment options or available financial assistance.'}
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-light text-[#14274B] text-center mb-4"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Frais de Scolarité 2026-2027' : 'Tuition Fees 2026-2027'}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-center text-[#475569] mb-12"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr' ? '1h30 par semaine · 32 semaines de cours par année académique' : '1.5 hours per week · 32 weeks of instruction per academic year'}
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="relative bg-white border-2 border-[#E7EAEF] p-8"
              >

                <div className="text-center mb-6">
                  <div
                    className="inline-block px-4 py-2 mb-4 text-sm font-medium text-white"
                    style={{ backgroundColor: plan.color, fontFamily: 'Poppins, sans-serif' }}
                  >
                    {plan.grades}
                  </div>
                  <h3
                    className="text-2xl font-semibold text-[#14274B] mb-2"
                    style={{ fontFamily: 'Gabarito, sans-serif' }}
                  >
                    {plan.title}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-[#14274B]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                      {plan.price}
                    </span>
                    <span className="text-2xl text-[#475569]">€</span>
                    <span className="text-[#475569] ml-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      /{language === 'fr' ? 'an' : 'year'}
                    </span>
                  </div>
                </div>

                <div className="space-y-4 pt-6 border-t border-[#E7EAEF]">
                  {plan.details.map((detail, i) => (
                    <div key={i} className="flex justify-between items-start">
                      <span className="text-[#475569] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {detail.label}
                        {detail.note && (
                          <span className="block text-xs text-[#94a3b8]">{detail.note}</span>
                        )}
                      </span>
                      <span className="text-[#14274B] font-semibold text-sm whitespace-nowrap ml-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        {detail.value}
                      </span>
                    </div>
                  ))}
                </div>

                <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer" className="block mt-8">
                  <Button
                    className="w-full py-6 bg-[#E7EAEF] text-[#14274B] hover:bg-[#2D78F6] hover:text-white"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {language === 'fr' ? 'S\'inscrire' : 'Enroll Now'}
                  </Button>
                </a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Terms */}
      <section className="py-16 bg-[#14274B]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl md:text-3xl font-light text-white mb-8 text-center"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Modalités de Paiement' : 'Payment Terms'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {paymentTerms.map((term, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="bg-white/10 p-6 text-center"
              >
                <term.icon className="w-8 h-8 text-[#2D78F6] mx-auto mb-3" />
                <p className="text-white/90" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {term.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sibling Discounts */}
      <section className="py-16 bg-[#E7EAEF]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-8">
            <Users className="w-8 h-8 text-[#66CA9D]" />
            <h2
              className="text-2xl md:text-3xl font-light text-[#14274B]"
              style={{ fontFamily: 'Gabarito, sans-serif' }}
            >
              {language === 'fr' ? 'Réduction Fratrie' : 'Sibling Discounts'}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {siblingDiscounts.map((discount, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="bg-white p-6 border-l-4 border-[#66CA9D]"
              >
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-4xl font-bold text-[#66CA9D]" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                    -{discount.discount}
                  </span>
                  <span className="text-xl font-semibold text-[#14274B]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {discount.child}
                  </span>
                </div>
                <p className="text-[#475569] text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {discount.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Financial Aid */}
      <section className="py-16 bg-[#2D78F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="w-12 h-12 text-white mx-auto mb-4" />
          <h2
            className="text-2xl md:text-3xl font-light text-white mb-4"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Aide Financière' : 'Financial Aid'}
          </h2>
          <p
            className="text-white/80 mb-6 max-w-2xl mx-auto"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr' 
              ? 'OFA France propose une aide financière aux familles qui en font la demande sous conditions de ressources.'
              : 'OFA France offers financial aid to families who apply and meet income requirements.'}
          </p>
          <a
            href="mailto:info@ofalycee.fr"
            className="inline-flex items-center gap-2 bg-white text-[#2D78F6] px-6 py-3 font-medium hover:bg-[#14274B] hover:text-white transition-colors"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {language === 'fr' ? 'Nous contacter' : 'Contact us'}
            <ArrowRight className="w-4 h-4" />
          </a>
          <p className="text-white/60 text-sm mt-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            info@ofalycee.fr
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-[#14274B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-12"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.tuition.includes.title}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {includes.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="flex items-start gap-4 p-6 bg-white/10"
              >
                <Check className="w-6 h-6 text-[#66CA9D] flex-shrink-0 mt-0.5" />
                <span
                  className="text-white"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {item}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D78F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.cta.title}
          </h2>
          <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="tuition-cta"
              size="lg"
              className="bg-white text-[#14274B] hover:bg-[#14274B] hover:text-white px-8 py-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.cta.primary}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
